import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  Radio,
  List,
  ListItem,
  ListItemText,
  Divider,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Category as CategoryIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import apiService from '../../services/api';
import { ServiceCategory, CreateServiceCategoryRequest, UpdateServiceCategoryRequest } from '../../types';
import { CATEGORY_ICON_OPTIONS, getCategoryIconEmoji } from '../../constants/categoryIcons';

const ServiceCategoriesPage: React.FC = () => {
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ServiceCategory | null>(null);
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  
  // Delete warning dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<ServiceCategory | null>(null);
  const [deleteAction, setDeleteAction] = useState<'move' | 'deactivate'>('move');
  const [targetCategoryId, setTargetCategoryId] = useState<number | ''>('');
  const [servicesInfo, setServicesInfo] = useState<{
    count: number;
    affectedProfessionals: number;
    services: Array<{ id: number; service_name: string; professional_id: number; professional_name: string }>;
  } | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    icon: '',
    is_active: true,
    sort_order: 0,
    lead_auto_closure_days: 14,
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  // Filter categories based on search query and status filter
  const filteredCategories = useMemo(() => {
    let filtered = categories;

    // Filter by search query (name and description). description/icon are nullable columns
    // (icon in particular - not every category has one set), so calling .toLowerCase() on
    // them unguarded throws and blanks the whole page the moment such a category is in the
    // current dataset - guard with `|| ''` for every field except name, which is required.
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(category =>
        category.name.toLowerCase().includes(query) ||
        (category.description || '').toLowerCase().includes(query) ||
        (category.icon || '').toLowerCase().includes(query)
      );
    }

    // Filter by status
    if (statusFilter === 'active') {
      filtered = filtered.filter(category => category.is_active);
    } else if (statusFilter === 'inactive') {
      filtered = filtered.filter(category => !category.is_active);
    }

    return filtered;
  }, [categories, searchQuery, statusFilter]);

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
  };

  const hasActiveFilters = searchQuery.trim() !== '' || statusFilter !== 'all';

  const fetchCategories = async () => {
    try {
      setIsLoading(true);
      setError('');
      const data = await apiService.getServiceCategories(true); // Include inactive
      // Show all categories (both active and inactive)
      setCategories(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error('Error fetching categories:', err);
      setError(err.response?.data?.message || 'Failed to load service categories');
      setCategories([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenDialog = (category?: ServiceCategory) => {
    if (category) {
      setEditingCategory(category);
      setFormData({
        name: category.name,
        description: category.description,
        icon: category.icon,
        is_active: category.is_active,
        sort_order: category.sort_order,
        lead_auto_closure_days: category.lead_auto_closure_days || 14,
      });
    } else {
      setEditingCategory(null);
      setFormData({
        name: '',
        description: '',
        icon: '',
        is_active: true,
        sort_order: 0,
        lead_auto_closure_days: 14,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCategory(null);
    setFormData({
      name: '',
      description: '',
      icon: '',
      is_active: true,
      sort_order: 0,
      lead_auto_closure_days: 14,
    });
  };

  const handleSubmit = async () => {
    try {
      if (editingCategory) {
        const updateData: UpdateServiceCategoryRequest = {
          name: formData.name,
          description: formData.description,
          icon: formData.icon,
          is_active: formData.is_active,
          sort_order: formData.sort_order,
          lead_auto_closure_days: formData.lead_auto_closure_days,
        };
        await apiService.updateServiceCategory(editingCategory.id, updateData);
      } else {
        const createData: CreateServiceCategoryRequest = {
          name: formData.name,
          description: formData.description,
          icon: formData.icon,
          is_active: formData.is_active,
          sort_order: formData.sort_order,
          lead_auto_closure_days: formData.lead_auto_closure_days,
        };
        await apiService.createServiceCategory(createData);
      }
      
      handleCloseDialog();
      fetchCategories();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save service category');
    }
  };

  const handleDeleteClick = async (id: number) => {
    const category = categories.find(cat => cat.id === id);
    if (!category) return;

    try {
      setError('');
      // Try to delete - if it has services, backend will return error with services info
      const result = await apiService.deleteServiceCategory(id);
      
      if (result.success) {
        // No services, deletion successful
        setSuccess(result.message || 'Service category deactivated successfully');
        setCategories(prevCategories => prevCategories.filter(cat => cat.id !== id));
        await fetchCategories();
        setTimeout(() => setSuccess(''), 3000);
      }
      } catch (err: any) {
      // Check if error contains services info
      if (err.response?.status === 422 && err.response?.data?.data) {
        const errorData = err.response.data.data;
        if (errorData.associated_services_count > 0) {
          // Show warning dialog with options
          setCategoryToDelete(category);
          setServicesInfo({
            count: errorData.associated_services_count,
            affectedProfessionals: errorData.affected_professionals_count || 0,
            services: errorData.services || []
          });
          setDeleteDialogOpen(true);
          return;
        }
      }
      
      // Other errors
      console.error('Error deleting category:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to delete service category';
      setError(errorMessage);
      alert(errorMessage);
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleConfirmDelete = async () => {
    if (!categoryToDelete) return;

    // Validate target category if moving
    if (deleteAction === 'move' && !targetCategoryId) {
      setError('Please select a target category to move services to');
      return;
    }

    try {
      setError('');
      setSuccess('');
      
      const result = await apiService.deleteServiceCategory(
        categoryToDelete.id,
        deleteAction,
        deleteAction === 'move' ? Number(targetCategoryId) : undefined
      );
      
      if (result.success) {
        setSuccess(result.message || 'Service category deactivated successfully');
        setCategories(prevCategories => prevCategories.filter(cat => cat.id !== categoryToDelete.id));
        await fetchCategories();
        setTimeout(() => setSuccess(''), 5000);
      } else {
        throw new Error(result.message || 'Failed to delete service category');
      }
      
      // Close dialog
      setDeleteDialogOpen(false);
      setCategoryToDelete(null);
      setDeleteAction('move');
      setTargetCategoryId('');
      setServicesInfo(null);
    } catch (err: any) {
      console.error('Error deleting category:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Failed to delete service category';
      setError(errorMessage);
      alert(errorMessage);
      }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setCategoryToDelete(null);
    setDeleteAction('move');
    setTargetCategoryId('');
    setServicesInfo(null);
  };

  const handleToggleActive = async (category: ServiceCategory) => {
    try {
      setError('');
      setSuccess('');
      const updateData: UpdateServiceCategoryRequest = {
        is_active: !category.is_active,
      };
      await apiService.updateServiceCategory(category.id, updateData);
      setSuccess(`Category ${!category.is_active ? 'activated' : 'deactivated'} successfully`);
      await fetchCategories();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      console.error('Error toggling category status:', err);
      const errorMessage = err.response?.data?.message || 'Failed to update category status';
      setError(errorMessage);
      setTimeout(() => setError(''), 5000);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      width: 70,
    },
    {
      field: 'name',
      headerName: 'Name',
      width: 200,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <CategoryIcon />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 300,
    },
    {
      field: 'icon',
      headerName: 'Icon',
      width: 140,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <span>{getCategoryIconEmoji(params.value)}</span>
          <Typography variant="body2" color="text.secondary">{params.value || '—'}</Typography>
        </Box>
      ),
    },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Active' : 'Inactive'}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'sort_order',
      headerName: 'Sort Order',
      width: 120,
    },
    {
      field: 'lead_auto_closure_days',
      headerName: 'Auto-Closure (Days)',
      width: 150,
      renderCell: (params) => {
        const days = params.value || 14;
        let color: 'default' | 'error' | 'warning' | 'success' = 'default';
        if (days < 7) color = 'error';
        else if (days <= 10) color = 'warning';
        else color = 'success';
        return (
          <Chip
            label={`${days} days`}
            color={color}
            size="small"
          />
        );
      },
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 150,
      renderCell: (params) => new Date(params.value).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 150,
      getActions: (params) => [
        <GridActionsCellItem
          key="toggle"
          icon={params.row.is_active ? <CancelIcon /> : <CheckCircleIcon />}
          label={params.row.is_active ? 'Deactivate' : 'Activate'}
          onClick={(e) => {
            e.stopPropagation();
            handleToggleActive(params.row);
          }}
          showInMenu={false}
        />,
        <GridActionsCellItem
          key="edit"
          icon={<EditIcon />}
          label="Edit"
          onClick={(e) => {
            e.stopPropagation();
            handleOpenDialog(params.row);
          }}
        />,
        <GridActionsCellItem
          key="delete"
          icon={<DeleteIcon />}
          label="Delete"
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteClick(params.row.id);
          }}
          showInMenu={false}
        />,
      ],
    },
  ];

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Service Categories</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Category
        </Button>
      </Box>

      {/* Search and Filter Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={2} alignItems="center">
            {/* Search Field */}
            <Box flex={{ xs: 1, md: 6 }}>
              <TextField
                fullWidth
                placeholder="Search categories by name, description, or icon..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton onClick={handleClearSearch} size="small">
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Status Filter */}
            <Box flex={{ xs: 1, md: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Status Filter</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status Filter"
                  onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  <MenuItem value="active">Active Only</MenuItem>
                  <MenuItem value="inactive">Inactive Only</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Clear Filters Button */}
            <Box flex={{ xs: 1, md: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                disabled={!hasActiveFilters}
              >
                Clear Filters
              </Button>
            </Box>
          </Box>

          {/* Results Summary */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Showing {filteredCategories.length} of {categories.length} categories
              {hasActiveFilters && ' (filtered)'}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          {filteredCategories.length === 0 && !isLoading ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={4}>
              <CategoryIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                {hasActiveFilters ? 'No categories found' : 'No categories available'}
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center">
                {hasActiveFilters 
                  ? 'Try adjusting your search or filter criteria'
                  : 'Start by adding your first service category'}
              </Typography>
              {hasActiveFilters && (
                <Button 
                  variant="outlined" 
                  onClick={handleClearFilters}
                  sx={{ mt: 2 }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>
          ) : (
            <DataGrid
              rows={Array.isArray(filteredCategories) ? filteredCategories : []}
              columns={columns}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 10 },
                },
              }}
              pageSizeOptions={[5, 10, 25]}
              disableRowSelectionOnClick
              autoHeight
            />
          )}
        </CardContent>
      </Card>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingCategory ? 'Edit Service Category' : 'Add Service Category'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
              multiline
              rows={3}
              required
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Icon</InputLabel>
              <Select
                value={formData.icon}
                label="Icon"
                onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              >
                {CATEGORY_ICON_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.emoji} {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Sort Order"
              type="number"
              value={formData.sort_order}
              onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Lead Auto-Closure Period (Days)"
              type="number"
              value={formData.lead_auto_closure_days}
              onChange={(e) => setFormData({ ...formData, lead_auto_closure_days: parseInt(e.target.value) || 14 })}
              margin="normal"
              helperText="Leads for professionals in this category will be auto-closed after this period if no response"
              inputProps={{ min: 1, max: 365 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active"
              sx={{ mt: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editingCategory ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Warning Dialog with Options */}
      <Dialog open={deleteDialogOpen} onClose={handleCancelDelete} maxWidth="md" fullWidth>
        <DialogTitle>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Cannot Delete Category with Associated Services
          </Alert>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            The category <strong>"{categoryToDelete?.name}"</strong> has <strong>{servicesInfo?.count} service(s)</strong> 
            {' '}from <strong>{servicesInfo?.affectedProfessionals} professional(s)</strong>.
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please choose how to handle these services before deleting the category:
          </Typography>

          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={deleteAction}
              onChange={(e) => setDeleteAction(e.target.value as 'move' | 'deactivate')}
            >
              <FormControlLabel
                value="move"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      Move Services to Another Category
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      All services will be moved to a selected category. Professionals will be notified.
                    </Typography>
                  </Box>
                }
              />
              {deleteAction === 'move' && (
                <Box sx={{ ml: 4, mt: 1, mb: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel>Select Target Category</InputLabel>
                    <Select
                      value={targetCategoryId}
                      label="Select Target Category"
                      onChange={(e) => setTargetCategoryId(e.target.value as number)}
                    >
                      {categories
                        .filter(cat => cat.id !== categoryToDelete?.id && cat.is_active)
                        .map((cat) => (
                          <MenuItem key={cat.id} value={cat.id}>
                            {cat.name}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Box>
              )}

              <FormControlLabel
                value="deactivate"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1" fontWeight="bold">
                      Deactivate Services
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      All services will be deactivated. Professionals will be notified to update their services.
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </FormControl>

          {servicesInfo && servicesInfo.services.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Affected Services:
              </Typography>
              <List dense sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                {servicesInfo.services.map((service, index) => (
                  <React.Fragment key={service.id}>
                    <ListItem>
                      <ListItemText
                        primary={service.service_name}
                        secondary={`Professional: ${service.professional_name}`}
                      />
                    </ListItem>
                    {index < servicesInfo.services.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={deleteAction === 'move' && !targetCategoryId}
          >
            Delete Category
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ServiceCategoriesPage;
